import { useMemo } from 'react'
import type { CustomerAccount, CustomerProfile } from '../../types'
import type { CustomerOrder } from '../../customerOrdersApi'
import type { CustomerSupportRequest } from '../../customerSupportApi'
import type { CustomerPrivacyDashboard } from '../../customerPrivacyApi'
import type { ShopLanguage, ShopLocale, ShopMessageKey } from '../../../i18n'
import { useCustomerDashboardSummary } from '../../hooks/useCustomerDashboardSummary'
import { buildCustomerAreaNavItems } from '../customer-area'
import { getAddressCount, getOpenTicketsCount, resolveAccountStatusLabel } from './dashboardShared'
import { toDashboardActivityItems } from './dashboardActivityHelpers'
import type { KpiShortcutItem, QuickActionItem } from './dashboardTypes'
import { buildDashboardKpiShortcuts, buildDashboardQuickActionCards } from './dashboardCardModels'
import { useDashboardOrderSelection } from './useDashboardOrderSelection'

type TranslateFn = (key: ShopMessageKey, params?: Record<string, string | number>) => string

type UseCustomerDashboardControllerParams = {
  account: CustomerAccount
  profile: CustomerProfile
  orders: CustomerOrder[]
  ordersLoading: boolean
  supportRequests: CustomerSupportRequest[]
  supportLoading: boolean
  privacyDashboard: CustomerPrivacyDashboard | null
  language: ShopLanguage
  locale: ShopLocale
  t: TranslateFn
  toLocalizedPath: (path: string) => string
  onNavigate: (targetPath: string) => void
}

/**
 * Composes the customer dashboard view-model from account/orders/support/privacy sources.
 *
 * @param params Dashboard data sources, locale context and navigation callback.
 * @returns Prepared dashboard state slices used by `CustomerDashboardPage`.
 */
export const useCustomerDashboardController = ({
  account,
  profile,
  orders,
  ordersLoading,
  supportRequests,
  supportLoading,
  privacyDashboard,
  language,
  locale,
  t,
  toLocalizedPath,
  onNavigate,
}: UseCustomerDashboardControllerParams) => {
  const isItalian = language === 'it'
  const summary = useCustomerDashboardSummary({
    account,
    profile,
    locale,
    t,
  })

  const {
    recentOrders,
    selectedOrderId,
    setSelectedOrderId,
    selectedOrder,
    selectedOrderProgress,
    selectedOrderNextStep,
  } = useDashboardOrderSelection({
    orders,
    language,
  })

  const openTicketsCount = useMemo(() => getOpenTicketsCount(supportRequests), [supportRequests])
  const activeOrdersCount = useMemo(
    () => orders.filter((order) => order.status === 'created' || order.status === 'paid').length,
    [orders],
  )
  const inDeliveryCount = useMemo(() => orders.filter((order) => order.status === 'shipped').length, [orders])
  const savedAddressesCount = useMemo(() => getAddressCount(profile), [profile])

  const navItems = useMemo(
    () =>
      buildCustomerAreaNavItems({
        t,
        toLocalizedPath,
        language,
        ordersBadge: orders.length > 0 ? String(orders.length) : undefined,
        supportBadge: openTicketsCount > 0 ? String(openTicketsCount) : undefined,
      }),
    [language, openTicketsCount, orders.length, t, toLocalizedPath],
  )

  const accountStatusLabel = resolveAccountStatusLabel(
    privacyDashboard?.account.accountStatus ?? account.accountStatus,
    language,
  )

  const localMeta = isItalian
    ? `Ultimo accesso: ${summary.lastLoginLabel} | Stato account: ${accountStatusLabel}`
    : `Last sign in: ${summary.lastLoginLabel} | Account status: ${accountStatusLabel}`

  const quickActionCards = useMemo<QuickActionItem[]>(
    () =>
      buildDashboardQuickActionCards({
        isItalian,
        onNavigate,
        toLocalizedPath,
      }),
    [isItalian, onNavigate, toLocalizedPath],
  )

  const kpiShortcuts = useMemo<KpiShortcutItem[]>(
    () =>
      buildDashboardKpiShortcuts({
        isItalian,
        ordersLoading,
        supportLoading,
        activeOrdersCount,
        inDeliveryCount,
        openTicketsCount,
        savedAddressesCount,
        locale,
        onNavigate,
        toLocalizedPath,
      }),
    [
      activeOrdersCount,
      inDeliveryCount,
      isItalian,
      locale,
      onNavigate,
      openTicketsCount,
      ordersLoading,
      savedAddressesCount,
      supportLoading,
      toLocalizedPath,
    ],
  )

  const activityItems = useMemo(
    () =>
      toDashboardActivityItems({
        recentOrders,
        supportRequests,
        privacyRequests: privacyDashboard?.requests,
        accountLastLoginAt: account.lastLoginAt,
        summaryLastLoginLabel: summary.lastLoginLabel,
        isItalian,
        language,
      }),
    [
      recentOrders,
      supportRequests,
      privacyDashboard?.requests,
      account.lastLoginAt,
      summary.lastLoginLabel,
      isItalian,
      language,
    ],
  )

  const activityDateFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat(locale, {
        dateStyle: 'medium',
        timeStyle: 'short',
      }),
    [locale],
  )

  const isDashboardBootstrapLoading = ordersLoading && supportLoading && orders.length === 0 && supportRequests.length === 0

  return {
    isItalian,
    summary,
    navItems,
    localMeta,
    quickActionCards,
    kpiShortcuts,
    recentOrders,
    selectedOrderId,
    setSelectedOrderId,
    selectedOrder,
    selectedOrderProgress,
    selectedOrderNextStep,
    activityItems,
    activityDateFormatter,
    isDashboardBootstrapLoading,
  }
}
